import axios from 'axios'
import inner from './NetteAjaxInner'

const CancelToken = axios.CancelToken

/**
 * AJAX Nette Framework
 *
 * @copyright Copyright (c) 2009, 2010 Jan Marek
 * @copyright Copyright (c) 2009, 2010 David Grudl
 * @copyright Copyright (c) 2012-2014 Vojtěch Dobeš
 * @license MIT
 *
 * @version 2.3.0
 */

export default new (class NetteAjax {
  $http = axios.create({
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })

  lastResponse = null

  constructor() {
    this.$http.interceptors.request.use((config) => {
      inner.fire(
        {
          name: 'start',
          off: config.off || [],
        },
        config
      )

      if (config.start) {
        config.start(config)
      }

      return config
    })
  }

  /**
   * Allows manipulation with extensions.
   * When called with 1. argument only, it returns extension with given name.
   * When called with 2. argument equal to false, it removes extension entirely.
   * When called with 2. argument equal to hash of event callbacks, it adds new extension.
   *
   * @param  {string} Name of extension
   * @param  {bool|object|null} Set of callbacks for any events OR false for removing extension.
   * @param  {object|null} Context for added extension
   * @return {object} returns extensions with given name
   */
  ext(name, callbacks, context) {
    if (typeof name === 'object') {
      return inner.ext(name, callbacks)
    } else if (callbacks === undefined) {
      return inner.contexts[name]
    } else if (!callbacks) {
      ;['init', 'load', 'prepare', 'before', 'start', 'success', 'complete', 'error'].forEach((event) => {
        inner.on[event][name] = undefined
      })
      return (inner.contexts[name] = undefined)
    } else if (typeof name === 'string' && inner.contexts[name] !== undefined) {
      throw new Error("Cannot override already registered nette-ajax extension '" + name + "'.")
    } else {
      return inner.ext(callbacks, context, name)
    }
  }

  /**
   * Initializes the plugin:
   * - fires 'init' event, then 'load' event
   * - when called with any arguments, it will override default 'init' extension
   *   with provided callbacks
   *
   * @param  {function|object|null} Callback for 'load' event or entire set of callbacks for any events
   * @param  {object|null} Context provided for callbacks in first argument
   * @return {$.nette} Provides a fluent interface
   */
  init(load, loadContext) {
    if (inner.initialized) throw new Error('Cannot initialize nette-ajax twice.')

    if (typeof load === 'function') {
      this.ext('init', null)
      this.ext(
        'init',
        {
          load: load,
        },
        loadContext
      )
    } else if (typeof load === 'object') {
      this.ext('init', null)
      this.ext('init', load, loadContext)
    } else if (load !== undefined) {
      throw new Error('Argument of init() can be function or function-hash only.')
    }

    inner.initialized = true

    inner.fire('init')
    this.load()
    return this
  }

  /**
   * Fires 'load' event
   *
   * @return {$.nette} Provides a fluent interface
   */
  load() {
    const self = this
    inner.fire('load', function (e) {
      self.ajax({}, this, e)
    })
    return this
  }

  getAjaxOff(ui) {
    if (!ui.getAttribute('data-ajax-off')) return null

    let off
    const rawOff = ui.getAttribute('data-ajax-off')
    /* if (rawOff.indexOf('[') === 0) {
      settings.off = $el.data('ajaxOff');
    } else */ if (rawOff.indexOf(',') !== -1) {
      off = rawOff.split(',')
    } else if (rawOff.indexOf(' ') !== -1) {
      off = rawOff.split(' ')
    } else {
      off = rawOff
    }

    if (typeof off === 'string') off = [off]
    off.map((val) => val.trim())

    return off
  }

  packageData(obj, form, namespace) {
    // if form is passed in through recursion assign otherwise create new
    const formData = form || new FormData()

    for (const property in obj) {
      let formKey

      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        if (namespace) {
          formKey = namespace + '[' + property + ']'
        } else {
          formKey = property
        }

        // if the property is an object, but not a File, use recursion.
        if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
          this.packageData(obj[property], formData, formKey)
        } else {
          // if it's a string or a File
          formData.append(formKey, obj[property])
        }
      }
    }
    return formData
  }

  async post(settings, data) {
    if (typeof settings === 'string') {
      settings = { url: settings }
    }

    settings = {
      ...settings,
      method: 'post',
      data: this.packageData(data),
    }

    return this.ajax(settings)
  }

  async ajax(settings, ui, e) {
    if (typeof settings === 'string') {
      settings = { url: settings }
    }

    if (!settings.nette && ui && e) {
      const uiTag = ui.tagName.toLowerCase()
      const analyze = (settings.nette = {
        e: e,
        ui: ui,
        isForm: uiTag === 'form',
        isSubmit: (uiTag === 'input' || uiTag === 'button') && ui.type.toLowerCase() === 'submit',
        isImage: uiTag === 'input' && ui.type.toLowerCase() === 'image',
        form: null,
      })

      if (analyze.isSubmit || analyze.isImage) {
        analyze.form = ui.form
      } else if (analyze.isForm) {
        analyze.form = analyze.el
      }

      if (!settings.url) {
        settings.url = analyze.form
          ? analyze.form.getAttribute('action') || window.location.pathname + window.location.search
          : ui.href
      }
      if (!settings.method) {
        settings.method = analyze.form ? analyze.form.getAttribute('method') : 'get'
      }

      settings.off = this.getAjaxOff(ui)
    }

    inner.fire(
      {
        name: 'prepare',
        off: settings.off || [],
      },
      settings
    )
    if (settings.prepare) {
      settings.prepare(settings)
    }

    const originalBeforeSend = settings.beforeSend
    let result = inner.fire(
      {
        name: 'before',
        off: settings.off || [],
      },
      settings
    )

    if ((result || result === undefined) && originalBeforeSend) {
      result = originalBeforeSend(settings)
    }

    if (!result) {
      return false
      // throw new Error('NetteAjax before send canceled')
    }

    let response = null

    if (settings.cachedData) {
      response = {
        data: settings.cachedData,
        config: settings,
        cached: true,
      }
    } else {
      settings.cancelTokenSource = CancelToken.source()
      settings.cancelToken = settings.cancelTokenSource.token
      try {
        response = await this.$http.request(settings)
      } catch (error) {
        if (error.response) {
          response = error.response
        }

        inner.fire(
          {
            name: 'error',
            off: settings.off || [],
          },
          error,
          settings
        )

        inner.fire(
          {
            name: 'complete',
            off: settings.off || [],
          },
          response,
          'error',
          settings
        )

        throw error
      }
    }

    const finalize = () => {
      inner.fire(
        {
          name: 'success',
          off: response.config.off || [],
        },
        response.data,
        response,
        response.config
      )

      if (typeof settings.onSuccess === 'function') {
        settings.onSuccess(response.data, response, response.config)
      }

      inner.fire(
        {
          name: 'complete',
          off: settings.off || [],
        },
        response,
        'success',
        settings
      )

      return response.data
    }

    this.lastResponse = response.data

    if (settings.wait) {
      return { data: response.data, settings, finalize }
    }

    return finalize()
  }
})()
